import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  applyLegalPlaceholders,
  legalAssetLanguage,
  legalConfig,
  legalDocumentUrl,
  parseLegalDocumentId,
} from './legalConfig';
import { parseLegalMarkdown, renderLegalInline } from './legalMarkdown';
import styles from './LegalDocumentPage.module.css';

export const LegalDocumentPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const { t, i18n } = useTranslation('auth');
  const language = legalAssetLanguage(i18n.language);
  const document = parseLegalDocumentId(documentId);
  const [markdown, setMarkdown] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!document) {
      setMarkdown(null);
      setError(t('legalNotFound'));
      return;
    }

    let cancelled = false;
    setMarkdown(null);
    setError(null);

    const load = async () => {
      try {
        const response = await fetch(legalDocumentUrl(document, language));
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const text = await response.text();
        if (!cancelled) {
          setMarkdown(applyLegalPlaceholders(text, language));
        }
      } catch {
        if (!cancelled) {
          setError(t('legalLoadFailed'));
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [document, language, t]);

  const nodes = markdown ? parseLegalMarkdown(markdown) : [];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <span className={styles.brandName}>{legalConfig.productName}</span>
          <span className={styles.brandMeta}>{t('legalDocuments')}</span>
        </div>
        <nav className={styles.nav} aria-label={t('legalDocuments')}>
          <Link to="/legal/terms">{t('termsOfService')}</Link>
          <Link to="/legal/privacy">{t('privacyPolicy')}</Link>
          <Link to="/legal/customer">{t('customerTerms')}</Link>
        </nav>
      </header>
      <article className={styles.article}>
        <Link className={styles.back} to="/login">
          {t('backToLogin')}
        </Link>
        {error ? <p className={styles.status}>{error}</p> : null}
        {!error && !markdown ? <p className={styles.status}>{t('legalLoading')}</p> : null}
        {nodes.map((node, index) => {
          if (node.type === 'ul') {
            return (
              <ul key={`${node.type}-${index}`}>
                {node.items.map((item, itemIndex) => (
                  <li key={itemIndex} dangerouslySetInnerHTML={renderLegalInline(item)} />
                ))}
              </ul>
            );
          }

          const Tag = node.type;
          return <Tag key={`${node.type}-${index}`} dangerouslySetInnerHTML={renderLegalInline(node.text)} />;
        })}
      </article>
    </div>
  );
};

export default LegalDocumentPage;
